import React, { useState } from 'react';
import { 
    Container, 
    Paper, 
    Typography, 
    Box, 
    Alert,
    Button,
    Snackbar
} from '@mui/material';
import { Email, AdminPanelSettings, Send, Refresh } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { sendEmailTemplate, getEmailVerificationActionSettings } from '../firebase';
import { sendEmailVerification } from 'firebase/auth';

export const ActivationRequired = ({ reason = "adminApproval" }) => {
    const { logout, user, claims, refreshAuthUser } = useAuth();
    const [isResending, setIsResending] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [resendError, setResendError] = useState(false);
    const [statusInfo, setStatusInfo] = useState("");

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const handleResendEmail = async () => {
        setIsResending(true);
        setResendError(false);
        setResendSuccess(false);
        setStatusInfo("");

        try {
            if (reason === "emailVerification") {
                await sendEmailVerification(user, getEmailVerificationActionSettings());
            } else {
                // Keep existing behavior for inactive users waiting for admin approval.
                const emailData = {
                    template: "register",
                    user: {
                        name: user?.displayName || claims?.name || 'Benutzer',
                        email: user?.email,
                        cny: claims?.cny || 'Unbekannt',
                        uid: user?.uid
                    },
                    form: {
                        tenant: claims?.tnt || 'Unbekannt',
                        accountID: claims?.aid || 'Unbekannt',
                        company: claims?.cny || 'Unbekannt',
                        registrationDate: new Date().toLocaleDateString('de-DE'),
                        registrationTime: new Date().toLocaleTimeString('de-DE')
                    },
                    currentYear: new Date().getFullYear(),
                    htmlMessage: ``
                };
                await sendEmailTemplate(emailData);
            }
            setResendSuccess(true);
        } catch (error) {
            console.error('Error resending email:', error);
            setResendError(true);
        } finally {
            setIsResending(false);
        }
    };

    const handleCheckStatus = async () => {
        setIsCheckingStatus(true);
        setStatusInfo("");
        try {
            const refreshed = await refreshAuthUser();
            if (!refreshed?.user?.emailVerified) {
                setStatusInfo("Ihre E-Mail-Adresse ist noch nicht bestätigt. Bitte klicken Sie zuerst auf den Link in der Verifizierungs-Mail.");
            } else if (refreshed?.claims?.role === "Inactive" || !refreshed?.claims?.role) {
                setStatusInfo("E-Mail bestätigt. Ihr Konto wartet noch auf Freigabe durch einen Litport Admin.");
            } else {
                setStatusInfo("Status aktualisiert. Ihr Konto ist freigegeben.");
            }
        } catch (error) {
            console.error('Error checking account status:', error);
            setStatusInfo("Status konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.");
        } finally {
            setIsCheckingStatus(false);
        }
    };

    const isEmailVerificationStep = reason === "emailVerification";

    return (
        <Container component="main" maxWidth="md" sx={{ mt: 8 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ mb: 3 }}>
                    <AdminPanelSettings 
                        sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} 
                    />
                    <Typography variant="h4" gutterBottom>
                        Account-Aktivierung erforderlich
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                    <Typography variant="body1" paragraph>
                        <strong>Ihr Account wurde erfolgreich erstellt!</strong>
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Bevor Sie Litport nutzen können, sind zwei Schritte erforderlich:
                        <br />
                        1. E-Mail-Adresse bestätigen
                        <br />
                        2. Freigabe durch einen Litport Admin
                    </Typography>
                </Alert>

                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Email sx={{ mr: 1 }} />
                        <Typography variant="h6">
                            Was passiert als nächstes?
                        </Typography>
                    </Box>
                    <Typography variant="body1" paragraph>
                        1. Öffnen Sie die Verifizierungs-Mail und bestätigen Sie Ihre E-Mail-Adresse<br/>
                        2. Ein Administrator wird über Ihre Registrierung benachrichtigt<br/>
                        3. Nach der Freigabe können Sie Litport nutzen
                    </Typography>
                </Box>

                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Ihre Registrierungsdaten:</strong><br/>
                        E-Mail: {user?.email}<br/>
                        Status: {isEmailVerificationStep ? "Wartet auf E-Mail-Bestätigung" : "Wartet auf Admin-Freigabe"}
                    </Typography>
                </Box>

                {statusInfo && (
                    <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                        {statusInfo}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                    <Button 
                        variant="contained" 
                        onClick={handleResendEmail}
                        disabled={isResending}
                        startIcon={<Send />}
                        color="primary"
                    >
                        {isResending ? 'Wird gesendet...' : isEmailVerificationStep ? 'Verifizierungs-Mail erneut senden' : 'Benachrichtigung erneut senden'}
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={handleCheckStatus}
                        disabled={isCheckingStatus}
                        startIcon={<Refresh />}
                    >
                        {isCheckingStatus ? 'Prüfe Status...' : 'Status prüfen'}
                    </Button>
                    
                    <Button 
                        variant="outlined" 
                        onClick={handleLogout}
                    >
                        Abmelden
                    </Button>
                </Box>

                {/* Success Snackbar */}
                <Snackbar
                    open={resendSuccess}
                    autoHideDuration={6000}
                    onClose={() => setResendSuccess(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={() => setResendSuccess(false)} 
                        severity="success" 
                        sx={{ width: '100%' }}
                    >
                        E-Mail erfolgreich erneut gesendet!
                    </Alert>
                </Snackbar>

                {/* Error Snackbar */}
                <Snackbar
                    open={resendError}
                    autoHideDuration={6000}
                    onClose={() => setResendError(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={() => setResendError(false)} 
                        severity="error" 
                        sx={{ width: '100%' }}
                    >
                        Fehler beim Senden der E-Mail. Bitte versuchen Sie es später erneut.
                    </Alert>
                </Snackbar>
            </Paper>
        </Container>
    );
};
